import {
  CSSProperties,
  SyntheticEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import SearchInput from "../../../components/SearchInput";
import { Button } from "../../../components/button/Button";
import ConfirmPopup from "../../../components/ConfirmPopup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faList,
  faArrowUpRightFromSquare,
  faXmark,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { deleteNameSet, getNamedSetList } from "../../../redux/queryMetaSlice";
import { showNotification } from "../../../redux/notificationSlice";
import Checkbox from "../../../components/checkbox/Checkbox";
import {
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CellStyleModule,
  FiltersToolPanelModule,
  SetFilterModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  DateFilterModule,
  ModuleRegistry,
  NumberFilterModule,
  TextFilterModule,
  ValidationModule,
  RowSelectionModule,
  RowSelectionOptions,
} from "ag-grid-community";
import useAgGridTheme from "../../../gridUtils/agGridTheme";
import {
  DEFAULT_COL_DEF,
  DEFAULT_GRID_OPTIONS,
} from "../../../gridUtils/gridUtils";

import {
  FilterBoxProps,
  FilterOption,
  TimeFilterOption,
  TimeCategoryOption,
} from "../../../interfaces/queryBuilderInterfaces";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";

ModuleRegistry.registerModules([
  RowSelectionModule,
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellStyleModule,
  SetFilterModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ValidationModule,
]);

const stopAgGridNativeEvent = (event: Event, preventDefault = true) => {
  event.stopPropagation();
  event.stopImmediatePropagation();
  if (preventDefault) {
    event.preventDefault();
  }
};

const stopAgGridReactEvent = (event: SyntheticEvent, preventDefault = true) => {
  event.stopPropagation();
  if (preventDefault) {
    event.preventDefault();
  }
  stopAgGridNativeEvent(event.nativeEvent as Event, preventDefault);
};

const NAMED_SET_DISPLAY_TRUNCATE_LENGTH = 20;

const truncateNamedSetLabel = (value: unknown) => {
  const text = String(value ?? "");
  return text.length > NAMED_SET_DISPLAY_TRUNCATE_LENGTH
    ? `${text.slice(0, NAMED_SET_DISPLAY_TRUNCATE_LENGTH)}...`
    : text;
};

const DELETE_WARNING_NOTE =
  "Deleting a Named Set also deletes all queries using it.";

const DeleteWarningNote = ({ marginTop = 0 }: { marginTop?: number }) => (
  <div
    role="alert"
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      marginTop,
      fontSize: 12,
      color: "#991b1b",
      background: "#fef2f2",
      border: "1px solid #fecaca",
      borderRadius: 4,
      padding: "8px 10px",
    }}
  >
    <FontAwesomeIcon
      icon={faTriangleExclamation}
      style={{ marginTop: 1, flexShrink: 0 }}
    />
    <span>
      <strong>Warning:</strong> {DELETE_WARNING_NOTE}
    </span>
  </div>
);

interface NoRowsOverlayProps {
  message?: string;
  buttonText?: string;
  onAction?: () => void;
}

const NoRowsOverlay = ({
  message = "No filter values found.",
  buttonText = "Reset Filters",
  onAction,
}: NoRowsOverlayProps) => {
  return (
    <div
      className="ag-overlay-no-rows-center"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "16px",
        pointerEvents: "auto",
      }}
    >
      <div style={{ fontSize: "13px", color: "#4b5563" }}>{message}</div>
      {onAction && (
        <Button
          color="primary"
          className="primary-color-text"
          variant="plain"
          id="btn-no-rows-create-named-set"
          style={{ cursor: "pointer" }}
          onClick={onAction}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
};

const MembersPopover = ({ data }: { data: any }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [popoverPos, setPopoverPos] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  }>({});
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const namedSetLabel =
    data?.caption || data?.altCaption || data?.name || "Named set";
  const namedSetLabelShort = truncateNamedSetLabel(namedSetLabel);

  const members = useMemo(() => {
    const raw = data?.properties?.members ?? "";
    if (!raw) return [];
    return raw
      .split(";")
      .map((item: string) => item.trim())
      .filter((item: string) => item.length > 0)
      .map((item: string) => {
        const pipeIndex = item.indexOf("|");
        if (pipeIndex >= 0) {
          return {
            uniqueName: item.slice(0, pipeIndex).trim(),
            caption: item.slice(pipeIndex + 1).trim(),
          };
        }
        return { uniqueName: item, caption: item };
      });
  }, [data]);

  const filteredMembers = useMemo(() => {
    if (!searchText) return members;
    const lower = searchText.toLowerCase();
    return members.filter(
      (m: any) =>
        m.caption.toLowerCase().includes(lower) ||
        m.uniqueName.toLowerCase().includes(lower),
    );
  }, [members, searchText]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 260;
    const popoverHeight = 280;
    const margin = 8;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceRight = window.innerWidth - rect.left;

    const pos: {
      top?: number;
      bottom?: number;
      left?: number;
      right?: number;
    } = {};

    // Vertical: prefer below the trigger, flip above if not enough space
    if (spaceBelow >= popoverHeight + margin) {
      pos.top = rect.bottom + margin;
    } else {
      pos.bottom = window.innerHeight - rect.top + margin;
    }

    // Horizontal: align left edge with trigger, flip right-aligned if overflows
    if (spaceRight >= popoverWidth + margin) {
      pos.left = rect.left;
    } else {
      pos.right = window.innerWidth - rect.right;
    }

    setPopoverPos(pos);
  }, []);

  // Native event listeners to prevent AG Grid from capturing the event
  useEffect(() => {
    const el = triggerRef.current;
    if (!el) return;

    const handleClick = (e: Event) => {
      stopAgGridNativeEvent(e);
      setIsOpen((prev) => !prev);
      setSearchText("");
    };

    el.addEventListener("mousedown", stopAgGridNativeEvent, true);
    el.addEventListener("click", handleClick, true);
    el.addEventListener("pointerdown", stopAgGridNativeEvent, true);

    return () => {
      el.removeEventListener("mousedown", stopAgGridNativeEvent, true);
      el.removeEventListener("click", handleClick, true);
      el.removeEventListener("pointerdown", stopAgGridNativeEvent, true);
    };
  }, []);

  // Update position when popover opens
  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearchText("");
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Close on outside click; keep open during scroll and reposition it.
  useEffect(() => {
    if (!isOpen) return;

    const handleOutside = (e: MouseEvent | PointerEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchText("");
      }
    };

    let rafId: number | null = null;
    const schedulePositionUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        updatePosition();
        rafId = null;
      });
    };

    document.addEventListener("pointerdown", handleOutside, true);
    document.addEventListener("mousedown", handleOutside, true);
    document.addEventListener("scroll", schedulePositionUpdate, true);
    window.addEventListener("resize", schedulePositionUpdate);

    return () => {
      document.removeEventListener("pointerdown", handleOutside, true);
      document.removeEventListener("mousedown", handleOutside, true);
      document.removeEventListener("scroll", schedulePositionUpdate, true);
      window.removeEventListener("resize", schedulePositionUpdate);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [isOpen, updatePosition]);

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <div
        ref={triggerRef}
        className={`namedset-action-icons ${isOpen ? "is-open" : ""}`}
        title="View members"
        aria-label={`View members of ${namedSetLabel}`}
      >
        <FontAwesomeIcon
          icon={faArrowUpRightFromSquare}
          size="2x"
          style={{ fontSize: "12px" }}
        />
      </div>

      {isOpen &&
        createPortal(
          <div
            className="members-popover"
            ref={popoverRef}
            style={{
              position: "fixed",
              ...popoverPos,
            }}
            onMouseDown={(e) => stopAgGridReactEvent(e, false)}
          >
            <div className="members-popover-header">
              <div className="members-popover-title-wrap">
                <span className="members-popover-title" title={namedSetLabel}>
                  {namedSetLabelShort} members
                </span>
              </div>
              <div
                className="members-popover-close"
                onClick={() => {
                  setIsOpen(false);
                  setSearchText("");
                }}
              >
                <FontAwesomeIcon icon={faXmark} />
              </div>
            </div>
            {/* <div className="members-popover-meta">
              {filteredMembers.length} of {members.length}
            </div> */}
            {/* {members.length > 5 && ( */}
              <div className="members-popover-search">
                <SearchInput
                  callback={(value) => setSearchText(value)}
                  debounce={150}
                  placeholder="Search members"
                  onClear={() => setSearchText("")}
                />
              </div>
            {/* )} */}
            <div className="members-popover-list">
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member: any, idx: number) => (
                  <div key={idx} className="members-popover-item">
                    <span className="members-popover-item-caption">
                      {member.caption}
                    </span>
                  </div>
                ))
              ) : (
                <div className="members-popover-empty">
                  {searchText ? "No matching members" : "No members"}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const SelTypeToggle = ({ data }: { data: any }) => {
  const [isList, setIsList] = useState(data?.selType === "List");
  const toggleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsList(data?.selType === "List");
  }, [data?.selType]);

  useEffect(() => {
    const el = toggleRef.current;
    if (!el) return;

    const handleClick = (e: Event) => {
      stopAgGridNativeEvent(e);

      const next = !isList;
      setIsList(next);
      if (data) {
        data.selType = next ? "List" : "Sum";
      }
    };

    el.addEventListener("pointerdown", stopAgGridNativeEvent, true);
    el.addEventListener("mousedown", stopAgGridNativeEvent, true);
    el.addEventListener("click", handleClick, true);

    return () => {
      el.removeEventListener("pointerdown", stopAgGridNativeEvent, true);
      el.removeEventListener("mousedown", stopAgGridNativeEvent, true);
      el.removeEventListener("click", handleClick, true);
    };
  }, [isList, data]);

  return (
    <div
      ref={toggleRef}
      className="namedset-action-icons"
      title={
        isList
          ? "List (click to switch to Sum)"
          : "Sum (click to switch to List)"
      }
      style={{
        width: "16px",
        height: "16px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        cursor: "pointer",
      }}
    >
      {isList ? (
        <FontAwesomeIcon icon={faList} size="2x" style={{ fontSize: "12px" }} />
      ) : (
        <span style={{ fontSize: "14px", fontWeight: 700, lineHeight: 1 }}>
          Σ
        </span>
      )}
    </div>
  );
};

const ActionIcon = ({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleClick = (e: Event) => {
      stopAgGridNativeEvent(e);
      onClick();
    };
    el.addEventListener("pointerdown", stopAgGridNativeEvent, true);
    el.addEventListener("mousedown", stopAgGridNativeEvent, true);
    el.addEventListener("click", handleClick, true);
    return () => {
      el.removeEventListener("pointerdown", stopAgGridNativeEvent, true);
      el.removeEventListener("mousedown", stopAgGridNativeEvent, true);
      el.removeEventListener("click", handleClick, true);
    };
  }, [onClick]);

  return (
    <div ref={ref} className="namedset-action-icons" title={title}>
      {children}
    </div>
  );
};

const NameSetBox = ({
  treeData,
  filterSelections,
  applyFilter,
  advancedFilterGridRef,
  advancedFilterGridColumn,
  handleCheckAll,
  handleUncheckAll,
  onRowSelected,
  onCellClicked,
  clearSelections,
  selectRowsByArray,
  setOptionsLength,
  currentSelections,
  isExclude,
  setExclude,
  setIsNameSetListMode,
  onEditNamedSet,
  onNamedSetListChange,
}: FilterBoxProps) => {
  const [filterOptions, setFilterOptions] = useState<TimeFilterOption[]>([]);

  const [options, setOptions] = useState<TimeFilterOption[]>([]);
  const [isShowSelected, setShowSelected] = useState(false);
  const [isShowMyNamedSets, setShowMyNamedSets] = useState(true);
  const [isLoading, setLoading] = useState(true);
  const [style, setStyle] = useState<CSSProperties>({ height: "220px" });
  const dispatch: AppDispatch = useDispatch();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showError, setShowError] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [confirmPopUp, setConfirmPopUp] = useState({
    title: "",
    message: "" as ReactNode,
    confirmAction: (title: string, id: number) => {},
    closeAction: () => {},
    id: 0,
  });

  const [searchText, setSearchText] = useState("");

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return { mode: "multiRow" };
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };
  const { user = null } = useSelector((state: RootState) => state.user);
  const showSelectedHandler = () => {
    handleShowSelectedInternal(
      !isShowSelected,
      undefined,
      undefined,
      searchText,
      isShowMyNamedSets,
    );
    setShowSelected(!isShowSelected);
  };

  const showMyNamedSetsHandler = () => {
    const next = !isShowMyNamedSets;
    handleShowSelectedInternal(
      isShowSelected,
      undefined,
      undefined,
      searchText,
      next,
    );
    setShowMyNamedSets(next);
  };

  const search = (value: string) => {
    //searchInternal(value);
    handleShowSelectedInternal(
      isShowSelected,
      undefined,
      undefined,
      value,
      isShowMyNamedSets,
    );
    setSearchText(value);
  };

  const createNameSet = () => {
    // if (clearSelections) {
    //   clearSelections();
    // }

    setIsNameSetListMode(false);
  };

  const levelUniqueName = treeData.uniquename
    ? treeData.uniquename
    : treeData.levels[0].uniquename;

  const isOwnedByCurrentUser = useCallback(
    (namedSet: any) => namedSet?.properties?.owner === user?.name,
    [user?.name],
  );

  const isRowSelectable = useCallback(
    (rowNode: any) => isOwnedByCurrentUser(rowNode?.data),
    [isOwnedByCurrentUser],
  );

  const handleDeleteNamedSet = (namedSet: any) => {
    if (!isOwnedByCurrentUser(namedSet)) {
      return;
    }

    const namedSetId =
      namedSet?.properties?.id ?? namedSet?.id ?? namedSet?.nsid ?? "";
    if (!namedSetId) {
      return;
    }

    const namedSetLabel =
      namedSet?.caption ||
      namedSet?.altCaption ||
      namedSet?.name ||
      "this named set";
    const namedSetLabelForMessage = truncateNamedSetLabel(namedSetLabel);

    setShowConfirmPopup(true);
    setConfirmPopUp({
      title: "Delete Named Set",
      message: (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div>{`Are you sure you want to delete ${namedSetLabelForMessage}?`}</div>
          <DeleteWarningNote marginTop={2} />
        </div>
      ),
      confirmAction: async () => {
        setShowConfirmPopup(false);
        setLoading(true);
        try {
          const result = await dispatch(
            deleteNameSet({
              nsids: [String(namedSetId)],
              levelName: levelUniqueName,
            }),
          );

          if (deleteNameSet.fulfilled.match(result)) {
            await fetchData(levelUniqueName);
          } else {
            const errorMessage =
              (result as any)?.payload?.message ||
              (result as any)?.error?.message ||
              "Something went wrong. Please try again.";
            dispatch(
              showNotification({
                message: errorMessage,
                duration: 3000,
                isError: true,
              }),
            );
          }
        } catch (error: any) {
          dispatch(
            showNotification({
              message:
                error?.message || "Something went wrong. Please try again",
              duration: 3000,
              isError: true,
            }),
          );
        } finally {
             dispatch(
              showNotification({
                message: "Named set deleted successfully.",
                duration: 3000,
                isError: false,
              }),
            );
          setLoading(false);
          clearSelections?.();
        } 
      },
      closeAction: () => {
        setShowConfirmPopup(false);
      },
      id: 0,
    });
  };

  const handleDeleteSelectedNamedSets = () => {
    if (!currentSelections || currentSelections.length === 0) {
      return;
    }

    const ownedSelections = currentSelections.filter((item: any) =>
      isOwnedByCurrentUser(item),
    );

    if (ownedSelections.length === 0) {
      return;
    }

    const selectedIds = ownedSelections
      .map(
        (item: any) =>
          item?.properties?.id ??
          item?.id ??
          item?.nsid ??
          (options.find((opt) => opt.name === item?.name)?.properties as any)
            ?.id,
      )
      .filter((id: any) => id !== null && id !== undefined && String(id) !== "")
      .map((id: any) => String(id));

    if (selectedIds.length === 0) {
      return;
    }

    const selectedNamedSets = ownedSelections.map(
      (item: any) =>
        item?.caption || item?.altCaption || item?.name || "Unnamed Named Set",
    );

    const selectedNamedSetsPreview = selectedNamedSets.slice(0, 5);
    const extraCount =
      selectedNamedSets.length - selectedNamedSetsPreview.length;

    const deleteConfirmationBody = (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 13 }}>
          Are you sure you want to delete the following named sets?
        </div>
        <ul
          style={{
            margin: 0,
            padding: 0,
            paddingLeft: 16,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {selectedNamedSetsPreview.map((label: string, index: number) => (
            <li key={`${label}-${index}`} style={{ marginBottom: 4 }}>
              {label}
            </li>
          ))}
        </ul>
        {extraCount > 0 && (
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            And {extraCount} more.
          </div>
        )}
        <DeleteWarningNote marginTop={8} />

        
      </div>
    );

    setShowConfirmPopup(true);
    setConfirmPopUp({
      title: "Delete Selected Named Sets",
      message: deleteConfirmationBody,
      confirmAction: async () => {
        setShowConfirmPopup(false);
        setLoading(true);
        try {
          const result = await dispatch(
            deleteNameSet({
              nsids: selectedIds,
              levelName: levelUniqueName,
            }),
          );

          if (deleteNameSet.fulfilled.match(result)) {
            await fetchData(levelUniqueName);
            handleUncheckAll?.();
            setShowSelected(false);
          } else {
            const errorMessage =
              (result as any)?.payload?.message ||
              (result as any)?.error?.message ||
              "Something went wrong. Please try again.";
            dispatch(
              showNotification({
                message: errorMessage,
                duration: 3000,
                isError: true,
              }),
            );
          }
        } catch (error: any) {
          dispatch(
            showNotification({
              message:
                error?.message || "Something went wrong. Please try again.",
              duration: 3000,
              isError: true,
            }),
          );
        } finally {
          setLoading(false);
           dispatch(
              showNotification({
                message: "Selected namedsets deleted successfully.",
                duration: 3000,
                isError: false,
              }),
            );
          clearSelections?.();
        }
      },
      closeAction: () => {
        setShowConfirmPopup(false);
      },
      id: selectedIds.length,
    });
  };

  const hasOwnedSelections = useMemo(
    () => !!currentSelections?.some((item: any) => isOwnedByCurrentUser(item)),
    [currentSelections, isOwnedByCurrentUser],
  );

  const actionColumnDef = {
    headerName: "Caption",
    field: "altCaption",
    flex: 1,
    cellRenderer: (params: any) => {
      const isOwner = isOwnedByCurrentUser(params.data);

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SelTypeToggle data={params.data} />
          <div
            style={{
              flex: 1,
              display: "flex",
              gap: 5,
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={params.value}
            >
              {truncateNamedSetLabel(params.value)}
            </span>
            <MembersPopover data={params.data} />
          </div>

          {isOwner && (
            <ActionIcon
              title="Edit named set"
              onClick={() => {
                // if (clearSelections) clearSelections();
                onEditNamedSet?.(params.data);
              }}
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </ActionIcon>
          )}
          {isOwner && (
            <ActionIcon
              title="Delete named set"
              onClick={() => handleDeleteNamedSet(params.data)}
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </ActionIcon>
          )}
        </div>
      );
    },
  };
  /*const applyFilterHandler = (applyOption: string) => {
        if(showError) {
            return;
        }
        applyFilter({ selectedFilters: currentSelections.map((i:any) => i.name), applyTo: applyOption, exclude: isExclude });
        close();
        defaultFilterSelections.selectedFilters = [];
    }
    const removeFilterHandler = () => {
        defaultFilterSelections.selectedFilters = [];
        applyFilter(defaultFilterSelections);
        close();
    }*/

  const fetchData = async (uniquename: string) => {
    setLoading(true);
    try {
      const data = await dispatch(getNamedSetList(uniquename));

      const currentSelTypeByName = new Map(
        (currentSelections || []).map((item: any) => [item.name, item.selType]),
      );
      const savedSelTypeByName = new Map(
        (filterSelections?.selectedFilters || []).map((item: any) => [
          item.name,
          item.selType,
        ]),
      );

      let filterData = data.payload as TimeFilterOption[];
      if (data.payload.length > 0) {
        filterData = data.payload.map((item: any) => {
          const selTypeFromSelections =
            currentSelTypeByName.get(item.name) ??
            savedSelTypeByName.get(item.name);

          return {
            ...item,
            isNamedSet: true,
            selType: selTypeFromSelections || item.selType || "Sum",
          };
        });
      }
      setOptions(filterData);
      const visibleData = isShowMyNamedSets
        ? filterData.filter(
            (item: any) => item?.properties?.owner === user?.name,
          )
        : filterData;
      setFilterOptions(visibleData);
      onNamedSetListChange?.(filterData);
      setOptionsLength(filterData ? filterData.length : 0);
    } finally {
      setLoading(false);
    }
  };
  const [selectedCategory, setSelectedCategory] =
    useState<TimeCategoryOption>();
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<TimeCategoryOption>();

  useEffect(() => {
    if (filterSelections && options && options.length > 0) {
      let selectedObjects: FilterOption[] = [];
      if (currentSelections) {
        selectedObjects = options.filter((item) =>
          currentSelections.map((i: any) => i.name).includes(item.name),
        );
      }

      selectRowsByArray?.(
        selectedObjects.map((item) => ({
          ...item,
          altCaption: item.caption, // Provide a default or derived value for altCaption
          properties: {}, // Provide a default or derived value for properties
        })),
      );
      //setExclude(filterSelections.exclude);
      // setApplyTo(filterSelections.applyTo);
      if (
        treeData.name === "Time Period" &&
        (!currentSelections || currentSelections.length === 0)
      ) {
        setShowError(true);
      } else {
        setShowError(false);
      }
    }
  }, [options, filterOptions, currentSelections]);

  useEffect(() => {
    if (showMenu) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
  }, [showMenu]);

  useEffect(() => {
    const gridApi = advancedFilterGridRef?.current?.api;
    if (!gridApi) {
      return;
    }

    if (isLoading) {
      gridApi.showLoadingOverlay();
      return;
    }

    if (!filterOptions || filterOptions.length === 0) {
      gridApi.showNoRowsOverlay();
      return;
    }

    gridApi.hideOverlay();
  }, [advancedFilterGridRef, isLoading, filterOptions]);

  const handleShowSelectedInternal = (
    flag: boolean,
    selectedOption1: TimeCategoryOption | undefined,
    selectedOption2: TimeCategoryOption | undefined,
    searchVal: string,
    showMyOnly?: boolean,
  ) => {
    let categoryToCheck: TimeCategoryOption | undefined = selectedOption1;
    let subCategoryToCheck: TimeCategoryOption | undefined = selectedOption2;

    if (categoryToCheck === undefined && subCategoryToCheck === undefined) {
      categoryToCheck = selectedCategory;
      subCategoryToCheck = selectedSubCategory;
    }

    let filteredOptions = options.filter((item) => {
      if (subCategoryToCheck) {
        return subCategoryToCheck.isMember(item);
      } else if (categoryToCheck) {
        return categoryToCheck.isMember(item);
      } else {
        return true;
      }
    });
    if (flag) {
      filteredOptions = filteredOptions.filter(
        (item) =>
          currentSelections &&
          currentSelections.some(
            (selectedRow: any) => selectedRow.name === item.name,
          ),
      );
    }
    if (showMyOnly) {
      filteredOptions = filteredOptions.filter(
        (item: any) => item?.properties?.owner === user?.name,
      );
    }
    if (searchVal !== "") {
      const searchItems = searchVal
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0);
      if (searchItems.length > 0) {
        const prefixMatches = filteredOptions.filter((item) =>
          searchItems.some((searchItem) =>
            item.caption.toLowerCase().startsWith(searchItem),
          ),
        );
        const containsMatches = filteredOptions.filter(
          (item) =>
            !searchItems.some((searchItem) =>
              item.caption.toLowerCase().startsWith(searchItem),
            ) &&
            searchItems.some((searchItem) =>
              item.caption.toLowerCase().includes(searchItem),
            ),
        );
        filteredOptions = [...prefixMatches, ...containsMatches];
      }
    }
    setFilterOptions(filteredOptions);
  };

  useEffect(() => {
    fetchData(levelUniqueName);
  }, [dispatch, levelUniqueName]);

  return (
    <div className="filter-dialog-container dialog-box-min filter-box">
      <div className="dialog-body nopadding">
        <div>
          <div className="search-filter">
            <SearchInput
              callback={search}
              debounce={300}
              placeholder="Search"
            />
          </div>
          <div
            className="filter-options-section"
            style={{ padding: "10px 0px 0px 0px" }}
          >
            <div className="filter-checkbox-section">
              <label className="radio-inline" htmlFor="show-selected-radio">
                <Checkbox
                  id="show-selected-radio"
                  name="show-selected"
                  checked={isShowMyNamedSets}
                  onChange={showMyNamedSetsHandler}
                  value={""}
                  styleClass={""}
                  color={""}
                />
                <span className="ellipsis">Show only my Named Sets</span>
              </label>
            </div>
          </div>
          <div className="filter-options-section">
            {/* <div className="filter-checkbox-section">
              <label className="radio-inline"  htmlFor="show-selected-radio">
                <Checkbox
                  id="show-selected-radio"
                  name="show-selected"
                  checked={isShowSelected}
                  onChange={showSelectedHandler}
                  value={""}
                  styleClass={""}
                  color={""}
                />
                <span className="ellipsis">Show Selected</span>
              </label>
            </div> */}

            <div className="filter-button-section" style={{ gap: 10 }}>
              <Button
                disabled={
                  (filterOptions && filterOptions.length > 999) ||
                  !filterOptions ||
                  filterOptions.length === 0
                }
                color="primary"
                style={{ padding: 0 }}
                className="btn md"
                variant="plain"
                id="btn-apply"
                onClick={handleCheckAll}
              >
                Select All
              </Button>
              <Button
                color="primary"
                disabled={!filterOptions || filterOptions.length === 0}
                className="btn md"
                style={{ padding: 0 }}
                variant="plain"
                id="btn-apply"
                onClick={handleUncheckAll}
              >
                Deselect All
              </Button>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                // marginBottom: "8px",
                padding: "4px 8px",
                gap: 15,
              }}
            >
              {hasOwnedSelections && (
                <Button
                  color="primary"
                  // className="btn md"
                  variant="plain"
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    background: "transparent",
                    border: "none",
                    fontSize: "12px",
                  }}
                  id="btn-delete-named-set"
                  onClick={handleDeleteSelectedNamedSets}
                >
                  <FontAwesomeIcon
                    size="2x"
                    style={{ fontSize: 12 }}
                    icon={faTrashCan}
                  />
                  Delete
                </Button>
              )}
              {filterOptions && filterOptions.length > 0 && (
                <Button
                  color="primary"
                  // className="btn md"
                  variant="plain"
                  className="primary-color-text"
                  style={{ cursor: "pointer", fontSize: "12px" }}
                  id="btn-create-named-set"
                  onClick={createNameSet}
                >
                  + Create New
                </Button>
              )}
            </div>
          </div>

          <div className="ag-theme-alpine grid-wrapper grid-multi-select">
            <div style={style}>
              <AgGridReact<TimeFilterOption>
                theme={useAgGridTheme("compact")}
                ref={advancedFilterGridRef}
                rowData={filterOptions}
                isRowSelectable={isRowSelectable}
                columnDefs={[
                  actionColumnDef,
                  ...(advancedFilterGridColumn || []).filter(
                    (col: any) => col.field !== "altCaption" || col.hide,
                  ),
                ]}
                noRowsOverlayComponent={NoRowsOverlay}
                noRowsOverlayComponentParams={{
                  message: "No Named Set created.",
                  buttonText: "+ Create Named Set",
                  onAction: createNameSet,
                }}
                defaultColDef={DEFAULT_COL_DEF}
                rowSelection={rowSelection}
                gridOptions={DEFAULT_GRID_OPTIONS}
                onRowSelected={onRowSelected}
                onCellClicked={onCellClicked}
                suppressContextMenu={true}
                headerHeight={0}
                onGridReady={(params) => {
                  if (isLoading) {
                    params.api.showLoadingOverlay();
                  } else if (!filterOptions || filterOptions.length === 0) {
                    params.api.showNoRowsOverlay();
                  } else {
                    params.api.hideOverlay();
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {showConfirmPopup && (
        <ConfirmPopup
          title={confirmPopUp.title}
          id={confirmPopUp.id}
          message={confirmPopUp.message}
          confirmAction={confirmPopUp.confirmAction}
          closeAction={confirmPopUp.closeAction}
        />
      )}
      {showError && (
        <div style={{ fontSize: "12px", color: "red" }}>
          Must select at least one filter value for this field
        </div>
      )}
    </div>
  );
};

export default NameSetBox;
